(() => {
  'use strict';

  const storage = {
    get(key, fallback) {
      try {
        const value = localStorage.getItem(key);
        return value ? JSON.parse(value) : fallback;
      } catch (_) {
        return fallback;
      }
    },
    set(key, value) {
      try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
    }
  };

  const escapeHTML = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const initMenu = () => {
    const button = document.querySelector('[data-goat-menu-toggle]');
    const nav = document.querySelector('[data-goat-primary-nav]');
    if (!button || !nav) return;

    const close = () => {
      nav.classList.remove('is-open');
      button.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('menu-open');
    };

    button.addEventListener('click', () => {
      const opening = !nav.classList.contains('is-open');
      nav.classList.toggle('is-open', opening);
      button.setAttribute('aria-expanded', String(opening));
      document.body.classList.toggle('menu-open', opening);
    });
    nav.addEventListener('click', (event) => {
      if (event.target.closest('a')) close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });
    window.addEventListener('resize', () => {
      if (window.innerWidth > 1050) close();
    });
  };

  const initTimeline = () => {
    const containers = document.querySelectorAll('[data-goat-timeline-app]');
    if (!containers.length || !Array.isArray(window.GOAT_TOY_TIMELINE)) return;

    containers.forEach((container) => {
      const all = window.GOAT_TOY_TIMELINE.slice();
      const grid = container.querySelector('[data-timeline-grid]');
      if (!grid) return;

      const search = container.querySelector('[data-timeline-search]');
      const decade = container.querySelector('[data-timeline-decade]');
      const category = container.querySelector('[data-timeline-category]');
      const sort = container.querySelector('[data-timeline-sort]');
      const count = container.querySelector('[data-timeline-count]');
      const summary = container.querySelector('[data-timeline-summary]');
      const more = container.querySelector('[data-timeline-more]');
      const mode = container.dataset.mode || 'full';
      const initialLimit = Number(container.dataset.limit || (mode === 'teaser' ? 6 : 18));
      const shopBase = container.dataset.shopBase || '/search?q=';
      let visible = initialLimit;
      let filtered = [];

      if (decade && decade.options.length <= 1) {
        [...new Set(all.map((item) => item.decade))].sort().forEach((value) => {
          decade.insertAdjacentHTML('beforeend', `<option value="${escapeHTML(value)}">${escapeHTML(value)}</option>`);
        });
      }
      if (category && category.options.length <= 1) {
        [...new Set(all.map((item) => item.category))].sort().forEach((value) => {
          category.insertAdjacentHTML('beforeend', `<option value="${escapeHTML(value)}">${escapeHTML(value)}</option>`);
        });
      }

      const votedYears = new Set(storage.get('goat_timeline_votes', []));

      const card = (item) => {
        const voted = votedYears.has(item.year);
        const votes = item.votes + (voted ? 1 : 0);
        const href = `${shopBase}${encodeURIComponent(item.searchQuery || item.name)}`;
        return `
          <article class="goat-year-card" data-year="${item.year}">
            <div class="goat-year-card-art">
              <span class="goat-year-pill">${item.year}</span>
              <span class="goat-year-emoji" aria-hidden="true">${escapeHTML(item.emoji)}</span>
              <span class="goat-year-category">${escapeHTML(item.category)}</span>
            </div>
            <div class="goat-year-card-copy">
              <h3>${escapeHTML(item.name)}</h3>
              <p>${escapeHTML(item.why)}</p>
              <div class="goat-year-card-actions">
                <button class="goat-legacy-vote${voted ? ' is-voted' : ''}" type="button" data-timeline-vote="${item.year}" aria-pressed="${voted}">
                  <span aria-hidden="true">▲</span><span>${votes.toLocaleString()}</span>
                  <span class="goat-visually-hidden">Vote for ${escapeHTML(item.name)}</span>
                </button>
                <a class="goat-year-shop" href="${href}">Shop this legacy →</a>
              </div>
            </div>
          </article>`;
      };

      const apply = () => {
        const query = (search?.value || '').trim().toLowerCase();
        const decadeValue = decade?.value || '';
        const categoryValue = category?.value || '';
        const sortValue = sort?.value || 'newest';

        filtered = all.filter((item) => {
          const haystack = `${item.year} ${item.name} ${item.category} ${item.why}`.toLowerCase();
          return (!query || haystack.includes(query)) &&
            (!decadeValue || item.decade === decadeValue) &&
            (!categoryValue || item.category === categoryValue);
        });

        filtered.sort((a, b) => {
          if (sortValue === 'oldest') return a.year - b.year;
          if (sortValue === 'az') return a.name.localeCompare(b.name);
          if (sortValue === 'votes') return b.votes - a.votes;
          return b.year - a.year;
        });

        const shown = mode === 'teaser' ? filtered.slice(0, initialLimit) : filtered.slice(0, visible);
        grid.innerHTML = shown.length ? shown.map(card).join('') : `
          <div class="goat-empty-state">
            <h3>No GOATs found</h3>
            <p>Try a different year, decade, category, or toy name.</p>
          </div>`;

        if (count) count.textContent = String(filtered.length);
        if (summary) summary.textContent = filtered.length === 100 ? 'The full century, from 1926 through 2025.' : `${filtered.length} matching picks.`;
        if (more) {
          const hasMore = mode !== 'teaser' && visible < filtered.length;
          more.hidden = !hasMore;
          more.textContent = hasMore ? `Show more (${filtered.length - visible} remaining)` : '';
        }
      };

      [search, decade, category, sort].filter(Boolean).forEach((control) => {
        control.addEventListener(control.tagName === 'INPUT' ? 'input' : 'change', () => {
          visible = initialLimit;
          apply();
        });
      });
      more?.addEventListener('click', () => {
        visible += 18;
        apply();
      });
      grid.addEventListener('click', (event) => {
        const button = event.target.closest('[data-timeline-vote]');
        if (!button) return;
        const year = Number(button.dataset.timelineVote);
        if (votedYears.has(year)) votedYears.delete(year);
        else votedYears.add(year);
        storage.set('goat_timeline_votes', [...votedYears]);
        apply();
        const updated = grid.querySelector(`[data-timeline-vote="${year}"]`);
        updated?.classList.add('goat-flash');
      });
      apply();
    });
  };

  const initCommunity = () => {
    document.querySelectorAll('[data-goat-community]').forEach((community) => {
      const feed = community.querySelector('[data-goat-feed]');
      const tabs = community.querySelectorAll('[data-feed-sort]');
      if (!feed) return;

      const posts = [...feed.querySelectorAll('.goat-post')];
      const voted = new Set(storage.get('goat_community_votes', []));

      posts.forEach((post, index) => {
        const id = post.dataset.postId || `post-${index}`;
        const button = post.querySelector('[data-post-vote]');
        const number = post.querySelector('[data-post-vote-count]');
        const base = Number(post.dataset.score || number?.textContent || 0);
        if (voted.has(id)) {
          button?.classList.add('is-voted');
          button?.setAttribute('aria-pressed', 'true');
          if (number) number.textContent = String(base + 1);
        }
        button?.addEventListener('click', () => {
          if (voted.has(id)) voted.delete(id); else voted.add(id);
          storage.set('goat_community_votes', [...voted]);
          const active = voted.has(id);
          button.classList.toggle('is-voted', active);
          button.setAttribute('aria-pressed', String(active));
          if (number) number.textContent = String(base + (active ? 1 : 0));
          button.classList.add('goat-flash');
          setTimeout(() => button.classList.remove('goat-flash'), 600);
        });
      });

      const sortPosts = (kind) => {
        const sorted = posts.slice().sort((a, b) => {
          if (kind === 'new') return Number(b.dataset.date || 0) - Number(a.dataset.date || 0);
          if (kind === 'top') return Number(b.dataset.score || 0) - Number(a.dataset.score || 0);
          const aHot = Number(a.dataset.score || 0) + Number(a.dataset.comments || 0) * 4 + Number(a.dataset.date || 0) / 100000;
          const bHot = Number(b.dataset.score || 0) + Number(b.dataset.comments || 0) * 4 + Number(b.dataset.date || 0) / 100000;
          return bHot - aHot;
        });
        sorted.forEach((post) => feed.appendChild(post));
      };

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          tabs.forEach((item) => item.classList.remove('is-active'));
          tab.classList.add('is-active');
          sortPosts(tab.dataset.feedSort || 'hot');
        });
      });

      community.querySelectorAll('[data-goat-poll-option]').forEach((button) => {
        button.addEventListener('click', () => {
          community.querySelectorAll('[data-goat-poll-option]').forEach((item) => item.classList.remove('is-voted'));
          button.classList.add('is-voted');
          storage.set('goat_poll_choice', button.dataset.goatPollOption);
          button.querySelector('strong')?.classList.add('goat-flash');
        });
      });
      const pollChoice = storage.get('goat_poll_choice', '');
      if (pollChoice) community.querySelector(`[data-goat-poll-option="${CSS.escape(pollChoice)}"]`)?.classList.add('is-voted');
    });
  };

  const initQuantity = () => {
    document.querySelectorAll('[data-quantity-control]').forEach((wrap) => {
      const input = wrap.querySelector('input[type="number"]');
      if (!input) return;
      wrap.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
        input.value = String(Math.max(Number(input.min || 1), Number(input.value || 1) - 1));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      wrap.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
        const max = Number(input.max || Infinity);
        input.value = String(Math.min(max, Number(input.value || 1) + 1));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  };

  const initShare = () => {
    document.querySelectorAll('[data-goat-share]').forEach((button) => {
      button.addEventListener('click', async () => {
        const data = { title: document.title, text: button.dataset.shareText || document.title, url: window.location.href };
        if (navigator.share) {
          try { await navigator.share(data); } catch (_) {}
        } else {
          try {
            await navigator.clipboard.writeText(window.location.href);
            const original = button.textContent;
            button.textContent = 'Link copied!';
            setTimeout(() => { button.textContent = original; }, 1600);
          } catch (_) {}
        }
      });
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    initMenu();
    initTimeline();
    initCommunity();
    initQuantity();
    initShare();
  });
})();
